import React from 'react';
import { AskAiSection, ECOSYSTEM_LOGO_SLOT_CLASS, buildAskAiPrompt, buildAskAiProviders, ecosystemLogoClass } from '@rehub/ask-ai';
import {
  Sparkles,
  LayoutTemplate,
  LayoutGrid,
  Type,
  Palette,
  Music,
  MapPinned,
  Server,
  Smartphone,
  Gift,
  ArrowUpRight,
} from 'lucide-react';
import { APP_TAGLINE } from '../constants';

type RehubApp = { name: string; tagline: string; description: string; url: string; logo: string; current?: boolean };

const REHUB_APPS: RehubApp[] = [
  { name: 'PhotoEdit', tagline: 'AI Photo Editing', description: 'Professional AI photo editing — tone, colour, detail and crop for every listing image.', url: 'https://photoedit.rehub.studio', logo: 'https://photoedit.rehub.studio/photoedit-logo.png' },
  { name: 'HDR Photo', tagline: 'AI HDR Merging', description: 'Merge bracketed exposures into studio-quality HDR, then enhance automatically.', url: 'https://hdrphoto.rehub.studio', logo: 'https://hdrphoto.rehub.studio/hdr-photo-logo.png' },
  { name: 'Photoclear.ai', tagline: 'AI Decluttering', description: 'Remove clutter and distractions instantly with AI to reveal clean, buyer-ready rooms.', url: 'https://photoclear.rehub.studio', logo: 'https://photoclear.rehub.studio/Photoclear Logo HR Trans.png' },
  { name: 'Virtual Staging', tagline: 'AI Virtual Staging', description: 'Transform empty rooms into beautifully furnished interiors in minutes.', url: 'https://staging.rehub.studio', logo: 'https://staging.rehub.studio/virtual-staging-logo-transparent.png' },
  { name: 'ImageMotion', tagline: 'AI Motion', description: 'Turn static listing photos into cinematic property videos automatically.', url: 'https://imagemotion.rehub.studio', logo: 'https://imagemotion.rehub.studio/Logo.webp' },
  { name: 'VideoMotion', tagline: 'AI Video Editing', description: 'Turn raw walkthrough footage into polished, social-ready video edits.', url: 'https://videomotion.rehub.studio', logo: 'https://videomotion.rehub.studio/logo.png' },
  { name: 'PropertyCuts', tagline: 'Video Assembler', description: 'Stitch photos, clips and floor plans into a finished property video.', url: 'https://propertycuts.rehub.studio', logo: 'https://propertycuts.rehub.studio/logo.png', current: true },
  { name: 'Hyper Dusk', tagline: 'AI Day-to-Dusk', description: 'Transform daytime exteriors into stunning dusk shots with AI-powered lighting.', url: 'https://hyperdusk.rehub.studio', logo: 'https://hyperdusk.rehub.studio/hyperdusk-logo-wordmark-alpha.png' },
  { name: 'Screen Record', tagline: 'Guided Recordings', description: 'Record guided 360° tours and screen walkthroughs with voice, camera and a shareable link.', url: 'https://record.rehub.studio', logo: '/screenrecord-logo.png' },
  { name: '360 Tours', tagline: 'Virtual Tours', description: 'Build immersive virtual tours for property listings and developments.', url: 'https://360tours.rehub.studio', logo: 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png' },
  { name: 'Planos', tagline: 'Interactive Floor Plans', description: 'Create interactive floor plans that let buyers explore properties visually.', url: 'https://planos.rehub.studio', logo: 'https://planos.rehub.studio/Planos Logo Red.png' },
  { name: 'Sketchplan', tagline: 'AI Floor Plans', description: 'Turn rough sketches into accurate, professional floor plans.', url: 'https://sketchplan.rehub.studio', logo: 'https://sketchplan.rehub.studio/cropped-Sketchplan-Logo1-removebg-preview.webp' },
];

const ASK_AI_PRODUCT = 'PropertyCuts';
const ASK_AI_PROVIDERS = buildAskAiProviders(ASK_AI_PRODUCT);
const ASK_AI_PROMPT = buildAskAiPrompt({
  productName: ASK_AI_PRODUCT,
  siteUrl: 'https://propertycuts.rehub.studio',
  extraFocus:
    'Focus on assembling photos, clips and floor plans into a finished property video using templates, not AI walkthrough editing or still-photo animation.',
});

const EcosystemGrid: React.FC = () => (
  <div className="max-w-6xl mx-auto mt-28">
    <div className="text-center mb-12">
      <p className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-4">The ReHub Ecosystem</p>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3">One platform. Every property marketing app.</h2>
      <p className="text-slate-400 max-w-2xl mx-auto">Each app is purpose-built for property and works beautifully on its own — or together as one seamless workflow.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {REHUB_APPS.map((app) => (
        <a
          key={app.name}
          href={app.url}
          target={app.current ? undefined : '_blank'}
          rel={app.current ? undefined : 'noopener noreferrer'}
          className="group flex flex-col rounded-2xl border border-slate-700/70 bg-slate-900/50 backdrop-blur-sm p-6 text-left transition-all hover:border-slate-500 hover:bg-slate-900/80"
        >
          <div className={`mb-5 ${ECOSYSTEM_LOGO_SLOT_CLASS}`}>
            <img src={app.logo} alt={app.name} className={ecosystemLogoClass(app.name)} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{app.tagline}</p>
          <p className="text-sm text-slate-400 leading-relaxed mb-5 grow">{app.description}</p>
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-200 group-hover:text-white transition-colors">
            {app.current ? "You're here" : 'Launch app'}
            {!app.current && <ArrowUpRight size={16} className="text-slate-500 group-hover:text-teal-300 transition-colors" />}
          </span>
        </a>
      ))}
    </div>
  </div>
);

// Feature tiles — honest about what PropertyCuts v1 actually does.
// Keeps the same 3×3 shape as apps/videomotion/components/LandingPage.tsx.
const PROPERTYCUT_FEATURES = [
  {
    title: 'Simple editor',
    description:
      'Choose landscape or portrait, drop in photos and clips, then trim, reorder and export.',
    icon: <LayoutTemplate size={18} />,
  },
  {
    title: 'Landscape or portrait',
    description:
      '16:9 at 1920×1080, or 9:16 at 1080×1920. Existing square projects still open.',
    icon: <LayoutGrid size={18} />,
  },
  {
    title: 'Details stay optional',
    description:
      'Export a cut with only media and music. Add a title, price or logo only if you want them on screen.',
    icon: <Type size={18} />,
  },
  {
    title: 'Your branding, carried over',
    description:
      'Logo, brand colour, and contact details come through from your ReHub profile. One brand, every video.',
    icon: <Palette size={18} />,
  },
  {
    title: 'Curated music',
    description:
      'Hand-picked tracks tuned to each template mood — calm, upbeat, modern. Commercial-use licensed.',
    icon: <Music size={18} />,
  },
  {
    title: 'Photos and clips',
    description:
      'Upload photos and video in any order. The timeline follows what you add — there is no fixed 15, 30 or 60 second template.',
    icon: <MapPinned size={18} />,
  },
  {
    title: 'Renders while you work',
    description:
      'Server-side renders — close the tab, walk the dog, come back to a finished MP4 in your library.',
    icon: <Server size={18} />,
  },
  {
    title: 'Mobile-friendly',
    description:
      'Kick off a render from your phone right after an in-person viewing. Desktop for edits, mobile for speed.',
    icon: <Smartphone size={18} />,
  },
  {
    title: '10 free credits to start',
    description:
      'New accounts get 10 free credits on signup — try the editor and your first render on a real listing before you commit.',
    icon: <Gift size={18} />,
  },
];

interface LandingPageProps {
  onSignUp: () => void;
  onLogin: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onSignUp, onLogin }) => (
  <main className="relative z-10">
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-900/30 backdrop-blur-sm border border-teal-500/20 text-teal-300 rounded-full text-sm font-medium mb-8">
          <Sparkles size={14} className="shrink-0" />
          Template-driven property videos
        </div>

        <div className="flex justify-center mb-6">
          <img
            src="/logo.png"
            alt="PropertyCuts"
            className="h-[96px] md:h-[112px] w-auto object-contain drop-shadow-[0_0_24px_rgba(255,255,255,0.12)]"
          />
        </div>

        <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
          {APP_TAGLINE}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4">
          <button
            onClick={onSignUp}
            className="group flex items-center gap-3 px-10 py-5 bg-teal-400 hover:bg-teal-300 text-slate-950 rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-black/30 hover:scale-105"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-950 text-teal-300 group-hover:rotate-[360deg] transition-transform duration-700">
              <Sparkles size={20} strokeWidth={2} />
            </span>
            Try PropertyCuts Free
          </button>
          <button
            onClick={onLogin}
            className="px-8 py-5 bg-slate-900/80 backdrop-blur-sm border-2 border-slate-700 hover:border-teal-400 text-slate-200 hover:text-white rounded-2xl font-semibold text-lg transition-all shadow-lg"
          >
            Sign In
          </button>
        </div>
        <p className="text-sm text-slate-400 mt-4">
          10 free credits on signup. No card required.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 text-left">
          {PROPERTYCUT_FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="p-6 bg-slate-900/70 backdrop-blur-md border border-slate-700/80 rounded-2xl hover:border-teal-500/40 transition-colors"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-teal-400 shrink-0">{feature.icon}</span>
                <h3 className="text-lg font-semibold text-slate-100">{feature.title}</h3>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      <EcosystemGrid />
      <AskAiSection
        productName={ASK_AI_PRODUCT}
        prompt={ASK_AI_PROMPT}
        providers={ASK_AI_PROVIDERS}
        description="Ask ChatGPT, Perplexity, Grok, Claude or Gemini about PropertyCuts’s video templates, pricing and whether it fits your listing workflow."
      />
    </div>
  </main>
);

export default LandingPage;
